import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion, useInView } from 'framer-motion';

// Feature cards with Material Symbols icons
const features = [
    { icon: 'verified', label: 'Clinically Achievable' },
    { icon: 'bolt', label: 'Ultra-Fast' },
    { icon: 'rocket_launch', label: 'No Learning Curve' },
    { icon: 'auto_awesome', label: 'Ultra-Realistic' },
    { icon: 'trending_up', label: '85% Predictive Precision' },
];

const ComparisonSection: React.FC = () => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [sliderPosition, setSliderPosition] = useState(50);
    const sectionRef = useRef(null);
    const isInView = useInView(sectionRef, { once: true, margin: '-100px' });

    // Using the 5th before/after pair
    const beforeImage = '/images/hero/5before.jpg';
    const afterImage = '/images/hero/5after.jpg';

    const updateSliderPosition = useCallback((clientX: number) => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        let x = clientX - rect.left;
        x = Math.max(0, Math.min(x, rect.width));
        const percentage = (x / rect.width) * 100;
        setSliderPosition(percentage);
    }, []);

    const handleStart = useCallback((clientX: number) => {
        setIsDragging(true);
        updateSliderPosition(clientX);
    }, [updateSliderPosition]);

    const handleMove = useCallback((clientX: number) => {
        if (!isDragging) return;
        updateSliderPosition(clientX);
    }, [isDragging, updateSliderPosition]);

    const handleEnd = useCallback(() => {
        setIsDragging(false);
    }, []);

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => handleMove(e.clientX);
        const handleTouchMove = (e: TouchEvent) => {
            if (isDragging) handleMove(e.touches[0].clientX);
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleEnd);
        window.addEventListener('touchmove', handleTouchMove, { passive: true });
        window.addEventListener('touchend', handleEnd);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleEnd);
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('touchend', handleEnd);
        };
    }, [isDragging, handleMove, handleEnd]);

    // Intro animation: slide from 0 to 50
    useEffect(() => {
        if (!isInView) return;
        let start: number | null = null;
        const duration = 1200;
        let animationId: number;

        const animate = (timestamp: number) => {
            if (!start) start = timestamp;
            const elapsed = timestamp - start;
            const progress = Math.min(elapsed / duration, 1);
            const ease = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
            setSliderPosition(ease * 50);
            if (progress < 1) {
                animationId = requestAnimationFrame(animate);
            }
        };

        const timeout = setTimeout(() => {
            animationId = requestAnimationFrame(animate);
        }, 300);

        return () => {
            clearTimeout(timeout);
            if (animationId) cancelAnimationFrame(animationId);
        };
    }, [isInView]);

    return (
        <section
            ref={sectionRef}
            className="relative py-24 md:py-32 overflow-hidden"
            aria-label="AI Smile Comparison"
        >
            {/* Ambient Glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-teal-500/5 blur-[150px] rounded-full pointer-events-none" />

            <div className="relative z-10 max-w-6xl mx-auto px-4 md:px-8">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.8, ease: [0.23, 1, 0.32, 1] }}
                    className="text-center mb-12 md:mb-16"
                >
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-teal-900/30 backdrop-blur-md border border-teal-500/30 text-teal-300 text-xs font-semibold mb-6 tracking-wide uppercase">
                        <span className="w-2 h-2 rounded-full bg-teal-400 animate-pulse shadow-[0_0_8px_rgba(45,212,191,0.8)]" />
                        AI Analysis Complete
                    </div>
                    <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight">
                        AI Digital Smile Design{' '}
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-300 via-teal-400 to-emerald-400">
                            Preview
                        </span>
                    </h2>
                    <p className="text-gray-400 text-lg md:text-xl max-w-2xl mx-auto">
                        See the transformation before treatment begins
                    </p>
                </motion.div>

                {/* Comparison Slider */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={isInView ? { opacity: 1, scale: 1, y: 0 } : {}}
                    transition={{ duration: 1, delay: 0.2, ease: [0.23, 1, 0.32, 1] }}
                    ref={containerRef}
                    className="relative w-full aspect-[16/10] md:aspect-[16/9] rounded-[2rem] md:rounded-[3rem] overflow-hidden cursor-ew-resize select-none bg-zinc-900 border border-white/10 shadow-[0_25px_50px_-12px_rgba(0,0,0,0.8),0_0_100px_-60px_rgba(20,184,166,0.3)] hover:shadow-[0_0_0_1px_rgba(20,184,166,0.3),0_30px_60px_-12px_rgba(0,0,0,0.9),0_0_120px_-50px_rgba(20,184,166,0.4)] transition-shadow duration-500"
                    onMouseDown={(e) => handleStart(e.clientX)}
                    onTouchStart={(e) => handleStart(e.touches[0].clientX)}
                >
                    {/* After Image (Base Layer - Full Width) */}
                    <div className="absolute inset-0">
                        <img
                            src={afterImage}
                            alt="AI Enhanced Smile"
                            className="w-full h-full object-cover"
                            loading="lazy"
                            draggable={false}
                        />
                        <div
                            className={`absolute top-6 right-6 px-4 py-2 bg-black/50 backdrop-blur-xl border border-white/10 rounded-lg text-xs font-bold uppercase tracking-wider text-white transition-all duration-300 ${sliderPosition > 85 ? 'opacity-0 translate-x-2' : 'opacity-100'
                                }`}
                        >
                            <span className="material-symbols-outlined text-sm mr-1.5 align-middle" style={{ fontSize: '14px' }}>auto_awesome</span>
                            AI Design
                        </div>
                    </div>

                    {/* Before Image (Overlay Layer - Uses clipPath for reveal) */}
                    <div
                        className="absolute inset-0"
                        style={{
                            clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`,
                            willChange: 'clip-path'
                        }}
                    >
                        <img
                            src={beforeImage}
                            alt="Original Smile"
                            className="w-full h-full object-cover"
                            loading="lazy"
                            draggable={false}
                        />
                        <div
                            className={`absolute top-6 left-6 px-4 py-2 bg-black/50 backdrop-blur-xl border border-white/10 rounded-lg text-xs font-bold uppercase tracking-wider text-white transition-all duration-300 ${sliderPosition < 15 ? 'opacity-0 -translate-x-2' : 'opacity-100'
                                }`}
                        >
                            Original
                        </div>
                    </div>

                    {/* Slider Divider Line */}
                    <div
                        className="absolute top-0 bottom-0 w-0.5 bg-white/80 shadow-[0_0_20px_rgba(255,255,255,0.5)] pointer-events-none"
                        style={{
                            left: `${sliderPosition}%`,
                            transform: 'translateX(-50%)',
                            willChange: 'left'
                        }}
                    />

                    {/* Slider Handle Button */}
                    <div
                        className="absolute top-1/2 pointer-events-none"
                        style={{
                            left: `${sliderPosition}%`,
                            transform: 'translate(-50%, -50%)',
                            willChange: 'left'
                        }}
                    >
                        <div
                            className={`w-14 h-14 md:w-16 md:h-16 rounded-full bg-zinc-900/90 backdrop-blur-xl border-2 border-white/30 shadow-[0_10px_30px_rgba(0,0,0,0.6)] flex items-center justify-center transition-all duration-200 ${isDragging ? 'scale-110 bg-teal-600 border-teal-300 shadow-[0_0_40px_rgba(20,184,166,0.6)]' : ''
                                }`}
                        >
                            <svg
                                className="w-6 h-6 text-white"
                                fill="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
                                <path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z" transform="rotate(180 12 12)" />
                            </svg>
                        </div>
                    </div>
                </motion.div>

                {/* Feature Cards with Material Icons */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.8, delay: 0.5, ease: [0.23, 1, 0.32, 1] }}
                    className="flex flex-wrap justify-center gap-3 md:gap-4 mt-10 md:mt-14"
                >
                    {features.map((feature, index) => (
                        <motion.div
                            key={feature.label}
                            initial={{ opacity: 0, y: 20 }}
                            animate={isInView ? { opacity: 1, y: 0 } : {}}
                            transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                            className="flex items-center gap-2.5 px-4 py-2.5 md:px-5 md:py-3 bg-zinc-900/50 backdrop-blur-md border border-white/10 rounded-full text-sm md:text-base font-medium text-white/90 hover:border-teal-500/40 hover:bg-teal-900/20 transition-all duration-300 cursor-default"
                        >
                            <span
                                className="material-symbols-outlined text-teal-400"
                                style={{ fontSize: '20px' }}
                            >
                                {feature.icon}
                            </span>
                            <span>{feature.label}</span>
                        </motion.div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
};

export default ComparisonSection;
